import { makeObservable, override } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { DocumentSourceTypeOption, documentSourceTypeSchema } from '@/entities/DocumentSourceType';
import { notificationsStore } from '@/stores/global/RootStore';
import { SetValueOptions } from '@/stores/interfaces/IFormField';
import { FieldSelectStore } from '@/stores/local/shared/form/FieldSelectStore';
import call from '@/utils/api';
import { BaseResponse } from '@/utils/api/types';
import { schemeParseMultiple } from '@/utils/schemeParse';

import { ConstructorParams, DocumentSourceTypesResponseApi, OptionsParams } from './types';

export class DocumentSourceTypeSelectStore extends FieldSelectStore<DocumentSourceTypeOption> {
  private readonly _onManualSelect?: () => void;

  constructor({ getOrganizationId, onManualSelect }: ConstructorParams) {
    super({
      required: true,
      getOptions: (params: OptionsParams) => this._fetchDocumentSourceTypes(params, getOrganizationId),
    });

    this._onManualSelect = onManualSelect;

    makeObservable(this, {
      setValueByKey: override,
    });
  }

  override setValueByKey(key: DocumentSourceTypeOption['key'], options?: SetValueOptions): void {
    super.setValueByKey(key, options);
    this._onManualSelect?.();
  }

  findOptionByName(name: string): DocumentSourceTypeOption | undefined {
    return this.options.find((option) => option.value === name);
  }

  private async _fetchDocumentSourceTypes(
    _params: OptionsParams,
    getOrganizationId: () => number,
  ): Promise<BaseResponse<{ items: DocumentSourceTypeOption[]; total: number }>> {
    const response = await call<DocumentSourceTypesResponseApi>({
      endpoint: ENDPOINTS.documents.documentSourceTypes(String(getOrganizationId())),
    });

    if (response.isError) {
      notificationsStore.showError(response.error.response?.data.message ?? 'Не удалось загрузить список источников');

      return {
        isError: true,
        error: response.error,
      };
    }

    const parsed = schemeParseMultiple(documentSourceTypeSchema, response.data.items);

    return {
      isError: false,
      data: {
        items: parsed,
        total: response.data.total ?? parsed.length,
      },
    };
  }
}
